import React, { useEffect, useRef, useState, SyntheticEvent } from "react";
import { Modal, Box, Typography, Grid, TextField, Button, useMediaQuery, useTheme, Paper, IconButton, Divider, CircularProgress, Backdrop } from "@mui/material";
import { Expense } from "../../../../models/expense";
import CloseIcon from '@mui/icons-material/Close';
import axios from "axios";
import moment from "moment-timezone";

interface MyModalProps {
  isOpen: boolean;
  onClose: () => void;
  collect_id: string;
  boxId: number;
  userId: string;
  selectedDate: string;
  showLoading: () => void;
  hideLoading: () => void;
  canSave: boolean;
  onAfterSubmit: (createdExpenses?: {
    id: number;
    expense_id: number;
    value: number;
    description: string;
    name: string;
    boxId: number;
  }[]) => Promise<void> | void;
}

const ExpenseCollect: React.FC<MyModalProps> = ({
  isOpen,
  onClose,
  collect_id,
  boxId,
  userId,
  selectedDate,
  showLoading,
  hideLoading,
  canSave,
  onAfterSubmit,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [values, setValues] = useState<{ [key: number]: number }>({});
  const [descriptions, setDescriptions] = useState<{ [key: number]: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);

  const modalStyle = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isSmallScreen ? "90%" : 600,
    minWidth: isSmallScreen ? 320 : "auto",
    maxHeight: "80vh",
    display: "flex",
    flexDirection: "column" as const,
    outline: "none",
  };

  useEffect(() => {
    if (isOpen) {
      setValues({});
      setDescriptions({});
      setIsSaving(false);

      const fetchData = async () => {
        try {
          const { data } = await axios.post('getexpensesbytypeops', { type_expense_id: 1, type_operation_id: 2 });
          setExpenses(data.data);
        } catch (error) {
          console.error('Error fetching expenses data:', error);
        }
      };

      fetchData();
    }
  }, [isOpen]);

  const handleValueChange = (expenseId: number, value: number) => {
    setValues(prev => ({ ...prev, [expenseId]: value }));
  };

  const handleDescriptionChange = (expenseId: number, description: string) => {
    setDescriptions(prev => ({ ...prev, [expenseId]: description }));
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (savingRef.current) return;
    if (!canSave) return;

    const expenseDate = moment
      .tz(selectedDate, 'YYYY-MM-DD', 'America/Bogota')
      .format('YYYY-MM-DD');

    const formData = expenses
      .filter(e => values[e.id] > 0)
      .map(e => ({
        description: descriptions[e.id] || "",
        value: values[e.id],
        expense_id: e.id,
        user_id: userId,
        collect_id: Number(collect_id),
        box_origin_id: boxId,
        date: expenseDate,
        type_operation_id: 2,
      }));

    if (formData.length === 0) {
      return;
    }

    try {
      savingRef.current = true;
      setIsSaving(true);
      showLoading();
      const { data } = await axios.post("/process/expensesbox", { expenses: formData });
      const createdExpenses = (data?.data || []).map((item: any) => {
        const expense = expenses.find(e => Number(e.id) === Number(item.expense?.id || item.expense_id));
        return {
          id: Number(item.id),
          expense_id: Number(item.expense?.id || item.expense_id),
          value: Number(item.value || 0),
          description: item.description || "",
          name: expense?.name || "",
          boxId,
        };
      });
      savingRef.current = false;
      setIsSaving(false);
      onClose();
      Promise.resolve(onAfterSubmit(createdExpenses)).catch((refreshError) => {
        console.error('Error refreshing expenses after save:', refreshError);
      });
    } catch (error) {
      console.error('Error:', error);
      savingRef.current = false;
      setIsSaving(false);
    } finally {
      hideLoading();
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={(_, reason) => {
        if (isSaving || reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
    >
      <>
      <Backdrop
        open={isSaving}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Paper sx={modalStyle}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          <Typography variant="h6">Registrar Gastos</Typography>
          <IconButton
            onClick={() => {
              if (!isSaving) onClose();
            }}
            sx={{ color: "white" }}
            aria-label="cerrar"
            disabled={isSaving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, overflowY: "auto", flexGrow: 1 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Fecha del registro: {moment.tz(selectedDate, 'YYYY-MM-DD', 'America/Bogota').format('DD/MM/YYYY')}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {expenses.length === 0 ? (
            <Typography variant="body1">No hay gastos disponibles.</Typography>
          ) : (
            expenses.map(expense => (
              <Box key={expense.id} sx={{ mb: 2.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{expense.name}</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Valor"
                      type="number"
                      size="small"
                      value={values[expense.id] || ""}
                      onChange={(e) => handleValueChange(expense.id, Number(e.target.value))}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Descripción"
                      size="small"
                      value={descriptions[expense.id] || ""}
                      onChange={(e) => handleDescriptionChange(expense.id, e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 2,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Button variant="outlined" onClick={() => {
            if (!isSaving) onClose();
          }} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={expenses.length === 0 || isSaving || !canSave}
            startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {isSaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </Box>
      </Paper>
      </>
    </Modal>
  );
};


export default ExpenseCollect;
